import React, { useEffect, useMemo, useState } from 'react';
import RGL, { WidthProvider, Layout } from 'react-grid-layout';
import { fetchColumnsData } from '../../dispatchers/ColumnSelectorDispatcher';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import Loader from '../common/Loader/Loader';
import {
  fetchRunlifeDashboard,
  fetchInstallationYears,
  fetchFilterDates,
  selectRunlifeDashboardError,
  selectRunlifeDashboardLoading,
  selectManufacturerInstallation,
  selectManufacturerRunlife,
  selectFieldInstallation,
  selectAverageRunlifeByField,
  selectBoxPlotChartStatistics,
  selectSelectedYear,
  selectStartDate,
  selectEndDate,
  selectSelectedDateType,
} from './RunlifeDarshboardSlice';
import { ToastServiceProvider } from '../../components/toast/ToastService';
import HeaderElements from '../navigation/components/HeaderElements';
import RunlifeBarChart from './widgets/RunlifeBarChart';
import RealLifeSummary from './widgets/summary';
import OverallStats from './widgets/overall-stats';
import RunlifeStatsChart from './widgets/RunlifeStatsChart';
import RunLifeFieldStats from './widgets/field-statistics';
import RunLifeFormationStats from './widgets/formation-stats';
import RunLifeManufacturerStats from './widgets/manufacturer-statistics';
import SelectedWellFilters from '../well-filters/SelectedWellFilters';
import { handleRemoveFilter } from '../well-filters/wellFilterUtils';
import './RunlifeDashboard.scss';
import RunLifeFilter from './RunLifeFilter';
import { ReactComponent as ReportIcon } from '../../images/file-download-02.svg';
import { handleGenerateReport } from './export-report/ExportReport';
import MenuNavIcon from '../navigation/components/NavMenuIcon';

const GridLayout = WidthProvider(RGL);

const RunlifeDashboard: React.FC = () => {
  const dispatch = useAppDispatch();

  // widgets layout data from server
  const runlifePageData = useAppSelector((state: any) => state.testWellColumnsSlice?.data?.widgets || []);

  // local loading / UI state
  const [isWidgetLoading, setIsWidgetLoading] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const sliceLoading = useAppSelector(selectRunlifeDashboardLoading);
  const sliceError = useAppSelector(selectRunlifeDashboardError);

  // selectors for data
  const manufacturerInstallation = useAppSelector(selectManufacturerInstallation);
  const manufacturerRunlife = useAppSelector(selectManufacturerRunlife);
  const fieldInstallation = useAppSelector(selectFieldInstallation);
  const averageRunlifeByField = useAppSelector(selectAverageRunlifeByField);
  const boxPlotChartStatistics = useAppSelector(selectBoxPlotChartStatistics);
  const selectedFilters = useAppSelector((state: any) => state?.WellFiltersSlice?.PCSelectedFilters ?? []);
  const pcWellIds = useAppSelector((state) => state.WellFiltersSlice.PCWellIds);
  const selectedYear = useAppSelector(selectSelectedYear);
  const startDate = useAppSelector(selectStartDate);
  const endDate = useAppSelector(selectEndDate);
  const selectedDateType = useAppSelector(selectSelectedDateType);

  // overall loading guard
  const isLoading = isWidgetLoading;
  const dataLoading = sliceLoading;

  // fetch columns/layout on mount
  useEffect(() => {
    const loadLayout = async () => {
      setIsWidgetLoading(true);
      try {
        await dispatch(fetchColumnsData(`runlife-dashboard`));
      } finally {
        setIsWidgetLoading(false);
      }
    };
    loadLayout();
  }, [dispatch]);

  const getFilterIds = (section: string) =>
    selectedFilters
      .filter((f: any) => f.section === section && typeof f.id === 'number')
      .map((f: any) => f.id)
      .join(',');

  useEffect(() => {
    dispatch(fetchInstallationYears());
    dispatch(fetchFilterDates());
  }, [dispatch]);

  useEffect(() => {
    const areaIds = getFilterIds('Area');
    const businessUnitIds = getFilterIds('Business unit');
    const fieldIds = getFilterIds('Field');

    const params: any = {};
    if (areaIds !== '') params.areaIds = areaIds;
    if (businessUnitIds !== '') params.businessUnitIds = businessUnitIds;
    if (fieldIds !== '') params.fieldIds = fieldIds;
    if (pcWellIds && pcWellIds.length > 0) params.wellIds = pcWellIds;
    if (selectedYear) params.year = selectedYear;
    if (selectedDateType) params.dateType = selectedDateType;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    dispatch(fetchRunlifeDashboard(Object.keys(params).length ? params : undefined));
  }, [dispatch, selectedFilters, pcWellIds]);

  // Helper function to get bar chart series config from widget data
  const getSeriesConfigFromWidget = (widgetKey: string) => {
    const widget = runlifePageData.find((w: any) => w.key === widgetKey);
    if (widget?.widgetProperty?.widgetProperties?.parameterConfig?.length > 0) {
      const parameterConfig = widget.widgetProperty.widgetProperties.parameterConfig;
      const defaultConfig = parameterConfig.find((config: any) => config.default) || parameterConfig[0];
      if (defaultConfig?.parameters?.length > 0) {
        return defaultConfig.parameters
          .map((param: any) => ({
            key: param.name || '',
            name: param.description || param.name || 'Unknown',
            color: param.lineColor || '#4294FF',
            selected: param.selected !== false,
          }))
          .filter((config: any) => config.key);
      }
    }
    return [];
  };

  // safe layout mapping
  const safeLayout = (runlifePageData: any[]): Layout[] => {
    if (!Array.isArray(runlifePageData)) return [];
    return runlifePageData
      .map((obj: any): Layout | null => {
        const lg = obj?.layout?.lg;
        if (!lg || !obj?.key) {
          // invalid widget layout — skip
          return null;
        }
        return {
          i: String(obj.key),
          x: lg.x,
          y: lg.y,
          w: lg.width,
          h: lg.height,
          minH: lg.minH ?? 1,
          minW: lg.minW ?? 6,
        };
      })
      .filter((item): item is Layout => item !== null);
  };
  const layoutMemo = useMemo(() => safeLayout(runlifePageData), [runlifePageData]);

  const handleRemoveFilterUtil = (filterToRemove: any | 'ALL') => {
    handleRemoveFilter(filterToRemove, selectedFilters, dispatch);
  };
  const updatedLayout = layoutMemo.map((item) => {
    switch (item.i) {
      case 'overall-statistics':
      case 'manufacturer-statistics':
        return { ...item, h: 18 };
      case 'field-statistics':
        return { ...item, h: 15 };
      case 'formation-statistics':
        return { ...item, h: 13 };
      default:
        return item;
    }
  });

  if (isLoading) return <Loader />;

  const hasSelectedFilters = Array.isArray(selectedFilters) && selectedFilters.length > 0;

  return (
    <ToastServiceProvider>
      <div className={`summary-dashboard runlife-dashboard ${hasSelectedFilters ? 'has-selected-filters' : ''}`}>
        {sliceError && (
          <div className='error-message' style={{ marginBottom: 12 }}>
            {sliceError}
          </div>
        )}

        {/* ===== Header section ===== */}
        <div className='summary-header-container'>
          <div className='group-status-top-row'>
            <div className='nav-menu-icon-header-container'>
              <MenuNavIcon />
              <span className='group-status-group-name'>ESP runlife dashboard</span>
            </div>
            <div className='custom-views-header-elements'>
              <HeaderElements showWellsTab={true} showFilterIcon={true} />
            </div>
          </div>
        </div>

        {/* ===== Filters Section (chips area) ===== */}
        <div className='summary-header-filters-section'>
          <SelectedWellFilters
            filters={selectedFilters}
            onRemoveFilter={handleRemoveFilterUtil}
            recommendationsPage={true}
          />
        </div>

        <RunLifeFilter
          reportButton={
            <button
              onClick={() => handleGenerateReport(setIsGeneratingReport)}
              className='runlife-generate-report-button'
              disabled={isGeneratingReport}
            >
              {isGeneratingReport ? (
                <>
                  <div className='spinner' style={{ width: '16px', height: '16px', marginRight: '4px' }}></div>
                  Generating...
                </>
              ) : (
                <>
                  <ReportIcon />
                  Report
                </>
              )}
            </button>
          }
        />

        {dataLoading ? (
          <Loader />
        ) : (
          <>
            <div className='real-life-summary-container' style={{ marginBottom: '-12px' }}>
              <RealLifeSummary />
            </div>
            <GridLayout
              layout={updatedLayout}
              cols={12}
              rowHeight={29}
              isDraggable={false}
              isResizable={true}
              className='layout-react-grid'
              useCSSTransforms={false}
              isDroppable={false}
            >
              {updatedLayout?.map((item: any) => (
                <div key={item.i} id={`grid-item-${item.i}`} className='grid-item'>
                  {item.i === 'manufacturer-installations' && (
                    <RunlifeBarChart
                      chartData={manufacturerInstallation}
                      seriesConfig={getSeriesConfigFromWidget('manufacturer-installations')}
                      widgetHeader={'Manufacturer installations'}
                      chartLabel={'Installations'}
                      categoryField='Manufacturer'
                      marginRight={true}
                    />
                  )}
                  {item.i === 'manufacturer-runlife' && (
                    <RunlifeBarChart
                      chartData={manufacturerRunlife}
                      seriesConfig={getSeriesConfigFromWidget('manufacturer-runlife')}
                      widgetHeader={'Manufacturer runlife'}
                      chartLabel={'Average runlife'}
                      categoryField='Manufacturer'
                    />
                  )}
                  {item.i === 'overall-statistics' && <OverallStats />}
                  {item.i === 'manufacturer-statistics' && <RunLifeManufacturerStats />}
                  {item.i === 'field-statistics' && <RunLifeFieldStats />}
                  {item.i === 'field-installations' && (
                    <RunlifeBarChart
                      chartData={fieldInstallation}
                      seriesConfig={getSeriesConfigFromWidget('field-installations')}
                      widgetHeader={'Field installations'}
                      chartLabel={'Installations'}
                    />
                  )}
                  {item.i === 'average-runlife-by-field' && (
                    <RunlifeBarChart
                      chartData={averageRunlifeByField}
                      seriesConfig={getSeriesConfigFromWidget('average-runlife-by-field')}
                      widgetHeader={'Average runlife by field'}
                      chartLabel={'Average runlife by field'}
                    />
                  )}
                  {item.i === 'runlife-stats-by-installnumber' && (
                    <RunlifeStatsChart
                      chartData={boxPlotChartStatistics}
                      widgetHeader={'Runlife stats by install number'}
                      chartLabel={'Runlife stats by install number'}
                    />
                  )}
                  {item.i === 'formation-statistics' && <RunLifeFormationStats />}
                </div>
              ))}
            </GridLayout>
          </>
        )}
      </div>
    </ToastServiceProvider>
  );
};

export default RunlifeDashboard;
